import { createSelector } from 'reselect';
// Import enumerations.
import {
	BroadLineOfBusiness,
	EligibilityStatusCode,
	NewBusinessApplicationStatus,
	Peril,
} from '@app/enumerations';
// Import state types.
import {
	AppState,
	NewBusinessApplicationState,
	PolicyState,
} from '@app/redux/states';
// Import props.
import {
	NewBusinessApplicationProps,
} from '@app/props';
// Import api models.
import {
	AcknowledgementText,
	BindingAuthorityEligibilityResult,
	BuildingResponseCommercialExistingWithStatus,
	DisqualifyingConditionsAgreement,
	EligibilityStatus,
	InsuredInfoOfac,
	NewBusinessApplication,
	NewBusinessApplicationDocument,
	NewBusinessApplicationStatusInfo,
	NewBusinessApplicationStatusSummary,
	PerilDeductible,
	PolicyCostLineItem,
	PossibleDisclosureText,
	PossiblePeril,
	Signature,
} from '@app/models/api';
// Import selectors.
import {
	makeAreAllNewBusinessApplicationSectionsCompleteSelector,
} from '@app/redux/selectors';
// Import base selectors.
import {
	getPoliciesState,
} from '@app/redux/selectors/base';
// Import services.
import {
	StringService,
} from '@app/services';

// Fetches the new business application state.
const getNewBusinessApplicationState = (state: AppState, props: NewBusinessApplicationProps): NewBusinessApplicationState | undefined => {
	let policyState: PolicyState | undefined = getPoliciesState(state, props);
	return policyState !== undefined
		? policyState.newBusinessApplication
		: undefined;
}

// Get New Business Application Selector.
const makeGetNewBusinessApplicationSelector = () => createSelector(
	[getNewBusinessApplicationState],
	(newBusinessApplicationState: NewBusinessApplicationState | undefined): NewBusinessApplication | undefined => {
		return newBusinessApplicationState?.newBusinessApplication ?? undefined
	}
);

// Get New Business Binding Authority Number Selector.
const makeGetNewBusinessBindingAuthorityNumberSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): string | undefined => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.productCode !== null
			? newBusinessApplication.productCode
			: undefined;
	}
);

// Get New Business Total Annual Cost Selector.
const makeGetNewBusinessTotalAnnualCostSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): number | undefined => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.cost !== null
			&& newBusinessApplication.cost.totalCost !== null
			? newBusinessApplication.cost.totalCost
			: undefined;
	}
);

// Get New Business Cost Line Items Selector.
const makeGetNewBusinessCostLineItemsSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): PolicyCostLineItem[] => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.cost !== null
			&& newBusinessApplication.cost.lineItems !== null
			? newBusinessApplication.cost.lineItems.sort((costLineItem1: PolicyCostLineItem, costLineItem2: PolicyCostLineItem) => {
				if (costLineItem1.lineItemNumber !== null
					&& costLineItem2.lineItemNumber !== null) {
					if (costLineItem1.lineItemNumber < costLineItem2.lineItemNumber) {
						return -1;
					}
					else if (costLineItem1.lineItemNumber > costLineItem2.lineItemNumber) {
						return 1;
					}
				}
				return 0;
			})
			: [];
	}
);

// Get New Business Eligibility Results Selector.
const makeGetNewBusinessEligibilityResultsSelector = () => createSelector(
	[getNewBusinessApplicationState],
	(newBusinessApplicationState: NewBusinessApplicationState | undefined): BindingAuthorityEligibilityResult[] => {
		return newBusinessApplicationState !== undefined
			? newBusinessApplicationState.bindingAuthorityEligibilityResults
			: [];
	}
);

// Get New Business Application Status Summary Selector.
const makeGetNewBusinessApplicationStatusSummarySelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): NewBusinessApplicationStatusSummary | undefined => {
		return newBusinessApplication?.status ?? undefined
	}
);

// Get New Business Application Ara Decision Reasons Selector.
const makeGetNewBusinessApplicationAraDecisionReasonsSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSummarySelector()],
	(newBusinessApplicationStatusSummary: NewBusinessApplicationStatusSummary | undefined): string[] => {
		return newBusinessApplicationStatusSummary?.applicationAraDecisionReasons ?? []
	}
);

// Get New Business Application Eligibility Status Selector.
const makeGetNewBusinessApplicationEligibilityStatusSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSummarySelector()],
	(newBusinessApplicationStatusSummary: NewBusinessApplicationStatusSummary | undefined): EligibilityStatus | undefined => {
		return newBusinessApplicationStatusSummary?.eligibility ?? undefined
	}
);

// Get New Business Application Status Info Selector.
const makeGetNewBusinessApplicationStatusInfoSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSummarySelector()],
	(newBusinessApplicationStatusSummary: NewBusinessApplicationStatusSummary | undefined): NewBusinessApplicationStatusInfo | undefined => {
		return newBusinessApplicationStatusSummary?.status ?? undefined
	}
);

// Is New Business Eligibility Checked Selector.
const makeIsNewBusinessEligibilityCheckedSelector = () => createSelector(
	[makeGetNewBusinessApplicationEligibilityStatusSelector()],
	(eligibilityStatus: EligibilityStatus | undefined): boolean => {
		return eligibilityStatus !== undefined
			&& eligibilityStatus.statusCode !== null
			&& eligibilityStatus.statusCode !== EligibilityStatusCode.NOT_CHECKED;
	}
);

// Get New Business Eligibility Errors Selector.
const makeGetNewBusinessEligibilityErrorsSelector = () => createSelector(
	[makeGetNewBusinessApplicationEligibilityStatusSelector()],
	(eligibilityStatus: EligibilityStatus | undefined): string[] => {
		return eligibilityStatus !== undefined
			&& eligibilityStatus.statusCode === EligibilityStatusCode.FAIL
			? eligibilityStatus.problems
			: [];
	}
);

// Get New Business Application Status Selector.
const makeGetNewBusinessApplicationStatusSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusInfoSelector()],
	(newBusinessApplicationStatusInfo: NewBusinessApplicationStatusInfo | undefined): NewBusinessApplicationStatus | undefined => {
		return newBusinessApplicationStatusInfo?.statusCode ?? undefined
	}
);

// Get New Business Application Broad Line Of Business Selector.
const makeGetNewBusinessApplicationBroadLineOfBusinessSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): BroadLineOfBusiness | undefined => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.broadLineOfBusiness !== null
			&& newBusinessApplication.broadLineOfBusiness.code !== null
			? newBusinessApplication.broadLineOfBusiness.code
			: undefined;
	}
);

// Get New Business Selected Perils Deductibles Selector.
const makeGetNewBusinessSelectedPerilsDeductiblesSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): PerilDeductible[] => {
		return newBusinessApplication !== undefined
			? newBusinessApplication.perilsDeductibles
			: [];
	}
);

// Get New Business Selected Peril Codes Selector.
const makeGetNewBusinessSelectedPerilCodesSelector = () => createSelector(
	[makeGetNewBusinessSelectedPerilsDeductiblesSelector()],
	(perilsDeductibles: PerilDeductible[]): Peril[] => {
		return perilsDeductibles.map((perilDeductible: PerilDeductible) => {
			return perilDeductible.perilCode !== null
				? perilDeductible.perilCode
				: Peril.NONE;
		}).filter((perilCode: Peril) => {
			return !String.isNullOrWhiteSpace(perilCode);
		});
	}
);

// Is New Business Earthquake Peril Selected Selector.
const makeIsNewBusinessEarthquakePerilSelectedSelector = () => createSelector(
	[makeGetNewBusinessSelectedPerilCodesSelector()],
	(selectedPerilCodes: Peril[]): boolean => {
		return selectedPerilCodes.find((selectedPerilCode: Peril) => selectedPerilCode === Peril.EARTHQUAKE) !== undefined;
	}
);

// Is New Business Flood Peril Selected Selector.
const makeIsNewBusinessFloodPerilSelectedSelector = () => createSelector(
	[makeGetNewBusinessSelectedPerilCodesSelector()],
	(selectedPerilCodes: Peril[]): boolean => {
		return selectedPerilCodes.find((selectedPerilCode: Peril) => selectedPerilCode === Peril.FLOOD) !== undefined;
	}
);

// Has Tria New Business Selector.
const makeHasTriaNewBusinessSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): boolean | undefined => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.includeTriaCoverage !== null
			? newBusinessApplication.includeTriaCoverage
			: undefined;
	}
);

// Get New Business Disqualifying Conditions Agreement Selector.
const makeGetNewBusinessDisqualifyingConditionAgreementSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): DisqualifyingConditionsAgreement | undefined => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.disqualifyingConditionsAgreement !== null
			? newBusinessApplication.disqualifyingConditionsAgreement
			: undefined;
	}
);

// Get New Business Disqualifying Conditions Agreement Version Number Selector.
const makeGetNewBusinessDisqualifyingConditionAgreementVersionNumberSelector = () => createSelector(
    [makeGetNewBusinessDisqualifyingConditionAgreementSelector()],
    (disqualifyingConditionsAgreement: DisqualifyingConditionsAgreement | undefined): number | undefined => {
        return disqualifyingConditionsAgreement?.version ?? undefined;
    }
);

// Get New Business Disqualifiers Answer Selector.
const makeGetNewBusinessDisqualifiersAnswerSelector = () => createSelector(
	[makeGetNewBusinessDisqualifyingConditionAgreementSelector()],
	(disqualifyingConditionsAgreement: DisqualifyingConditionsAgreement | undefined): boolean | undefined => {
		return disqualifyingConditionsAgreement !== undefined
			&& disqualifyingConditionsAgreement.riskMeetsEligibilityRequirements !== null
			? disqualifyingConditionsAgreement.riskMeetsEligibilityRequirements
			: undefined;
	}
);

// Get New Business Insured Info Selector.
const makeGetNewBusinessInsuredInfoSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): InsuredInfoOfac | undefined => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.namedInsured !== null
			? newBusinessApplication.namedInsured
			: undefined;
	}
);

// Get New Business Insured Full Name Selector.
const makeGetNewBusinessInsuredFullNameSelector = () => createSelector(
	[makeGetNewBusinessInsuredInfoSelector()],
	(insuredInfo: InsuredInfoOfac | undefined): string | undefined => {
		if (insuredInfo !== undefined
			&& insuredInfo.name !== null) {
			// Get insured full name.
			const insuredFullName: string = [insuredInfo.name.firstNameOrCompanyName, insuredInfo.name.lastName]
				.filter((nameToken: string | null) => !String.isNullOrWhiteSpace(nameToken))
				.join(' ');
			return !String.isNullOrWhiteSpace(insuredFullName) ? insuredFullName : undefined;
		}
		return undefined;
	}
);

// Get New Business Inception Date Selector.
const makeGetNewBusinessInceptionDateSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): Date | undefined => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.requestedInceptionDate !== null
			? new Date(newBusinessApplication.requestedInceptionDate)
			: undefined;
	}
);

// Get New Business Expiration Date for Approved application Selector.
const makeGetNewBusinessExpirationDateSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): Date | undefined => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.expirationDate !== null
			? new Date(newBusinessApplication.expirationDate)
			: undefined;
	}
);

// Get New Business Disclosure Acknowledgements Selector.
const makeGetNewBusinessDisclosureAcknowledgementsSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): AcknowledgementText[] => {
		return newBusinessApplication !== undefined
			? newBusinessApplication.acknowledgements
			: [];
	}
);

// Get New Business Property State Selector.
const makeGetNewBusinessPropertyStateSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): string | undefined => {
		let propertyState: string | undefined = undefined;
		if (newBusinessApplication !== undefined) {
			if (newBusinessApplication.risk !== undefined) {
				propertyState = newBusinessApplication.risk?.address?.address?.state ?? undefined;
			}
			else if (newBusinessApplication.risks.length > 0) {
				let propertyStates: string[] = newBusinessApplication.risks.map((risk: BuildingResponseCommercialExistingWithStatus) => {
					return risk.address?.address?.state ?? String.empty();
				}).filter((propertyState: string | undefined) => {
					return !String.isNullOrWhiteSpace(propertyState);
				});
				propertyState = propertyStates.length > 0 ? propertyStates[0] : undefined;
			}
		}
		return propertyState;
	}
);

// Get New Business Id Selector.
const makeGetNewBusinessIdSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): number | undefined => {
		return newBusinessApplication?.id ?? undefined;
	}
);

// Get New Business Possible Disclosure Texts Selector.
const makeGetNewBusinessPossibleDisclosureTextsSelector = () => createSelector(
	[getNewBusinessApplicationState],
	(newBusinessApplicationState: NewBusinessApplicationState | undefined): PossibleDisclosureText[] => {
		return newBusinessApplicationState !== undefined
			? newBusinessApplicationState.possibleDisclosureTexts
			: [];
	}
);

// Get New Business Application Signature Selector.
const makeGetNewBusinessApplicationSignatureSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): Signature | undefined => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.signature !== null
			? newBusinessApplication.signature
			: undefined;
	}
);

// Get New Business Application Document Selector.
const makeGetNewBusinessApplicationDocumentSelector = () => createSelector(
	[getNewBusinessApplicationState],
	(newBusinessApplicationState: NewBusinessApplicationState | undefined): NewBusinessApplicationDocument | undefined => {
		return newBusinessApplicationState !== undefined
			? newBusinessApplicationState.newBusinessApplicationDocument
			: undefined;
	}
);

// Get New Business Application Document Name Selector.
const makeGetNewBusinessApplicationDocumentNameSelector = () => createSelector(
	[makeGetNewBusinessApplicationDocumentSelector()],
	(newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined): string | undefined => {
		return newBusinessApplicationDocument !== undefined
			&& newBusinessApplicationDocument.documentName !== null
			? newBusinessApplicationDocument.documentName
			: undefined;
	}
);

// Get New Business Application Document Url Selector.
const makeGetNewBusinessApplicationDocumentUrlSelector = () => createSelector(
	[makeGetNewBusinessApplicationDocumentSelector()],
	(newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined): string | undefined => {
		return newBusinessApplicationDocument !== undefined
			&& newBusinessApplicationDocument.documentUrl !== null
			? newBusinessApplicationDocument.documentUrl
			: undefined;
	}
);

// Get New Business Application Document Message Selector.
const makeGetNewBusinessApplicationDocumentMessageSelector = () => createSelector(
	[makeGetNewBusinessApplicationDocumentSelector()],
	(newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined): string | undefined => {
		return newBusinessApplicationDocument !== undefined
			&& newBusinessApplicationDocument.message !== null
			? newBusinessApplicationDocument.message
			: undefined;
	}
);

// Is New Business Application Document Available Selector.
const makeIsNewBusinessApplicationDocumentAvailableSelector = () => createSelector(
	[makeGetNewBusinessApplicationDocumentSelector()],
	(newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined): boolean => {
		return newBusinessApplicationDocument !== undefined
			&& !String.isNullOrWhiteSpace(newBusinessApplicationDocument.documentUrl);
	}
);

// Is New Business Application Submitted Selector.
const makeIsNewBusinessApplicationSubmittedSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSelector()],
	(newBusinessApplicationStatus: NewBusinessApplicationStatus | undefined): boolean => {
		return newBusinessApplicationStatus !== undefined
			? newBusinessApplicationStatus === NewBusinessApplicationStatus.SUBMITTED
			: false;
	}
);

// Is New Business Application Approved Selector.
const makeIsNewBusinessApplicationApprovedSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSelector()],
	(newBusinessApplicationStatus: NewBusinessApplicationStatus | undefined): boolean => {
		return newBusinessApplicationStatus !== undefined
			? newBusinessApplicationStatus === NewBusinessApplicationStatus.APPROVED
			: false;
	}
);

// Is New Business Application Approved Paid Selector.
const makeIsNewBusinessApplicationApprovedPaidSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSelector()],
	(newBusinessApplicationStatus: NewBusinessApplicationStatus | undefined): boolean => {
		return newBusinessApplicationStatus !== undefined
			? newBusinessApplicationStatus === NewBusinessApplicationStatus.APPROVED_PAID
			: false;
	}
);

// Is New Business Application Declined Selector.
const makeIsNewBusinessApplicationDeclinedSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSelector()],
	(newBusinessApplicationStatus: NewBusinessApplicationStatus | undefined): boolean => {
		return newBusinessApplicationStatus !== undefined
			? newBusinessApplicationStatus === NewBusinessApplicationStatus.DECLINED
			: false;
	}
);

// Is New Business Application On Hold Selector.
const makeIsNewBusinessApplicationOnHoldSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSelector()],
	(newBusinessApplicationStatus: NewBusinessApplicationStatus | undefined): boolean => {
		return newBusinessApplicationStatus !== undefined
			? newBusinessApplicationStatus === NewBusinessApplicationStatus.HOLD
			: false;
	}
);

// Is New Business Application In The Works Selector.
const makeIsNewBusinessApplicationInTheWorksSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSelector()],
	(newBusinessApplicationStatus: NewBusinessApplicationStatus | undefined): boolean => {
		return newBusinessApplicationStatus !== undefined
			? newBusinessApplicationStatus === NewBusinessApplicationStatus.IN_THE_WORKS
			: false;
	}
);

// Is New Business Application Loaded Selector.
const makeIsNewBusinessApplicationLoadedSelector = () => createSelector(
	[getNewBusinessApplicationState],
	(newBusinessApplicationState: NewBusinessApplicationState | undefined): boolean => {
		return newBusinessApplicationState !== undefined
			? newBusinessApplicationState.isNewBusinessApplicationLoaded
			: false;
	}
);

// Is New Business Application Not Submitted Selector.
const makeIsNewBusinessApplicationNotSubmittedSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSelector()],
	(newBusinessApplicationStatus: NewBusinessApplicationStatus | undefined): boolean => {
		return newBusinessApplicationStatus === NewBusinessApplicationStatus.IN_THE_WORKS
			|| newBusinessApplicationStatus === NewBusinessApplicationStatus.NEW
			|| newBusinessApplicationStatus === NewBusinessApplicationStatus.REVIEW;
	}
);

// Is New Business Application Submitted Or Has Underwriting Decision Selector.
const makeIsNewBusinessApplicationSubmittedOrHasUnderwritingDecisionSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSelector()],
	(newBusinessApplicationStatus: NewBusinessApplicationStatus | undefined): boolean => {
		return newBusinessApplicationStatus === NewBusinessApplicationStatus.APPROVED
			|| newBusinessApplicationStatus === NewBusinessApplicationStatus.DECLINED
			|| newBusinessApplicationStatus === NewBusinessApplicationStatus.HOLD
			|| newBusinessApplicationStatus === NewBusinessApplicationStatus.SUBMITTED;
	}
);

// Get New Business Unsubmission Confirmation Message Selector.
const makeGetNewBusinessUnsubmissionConfirmationMessageSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSelector()],
	(newBusinessApplicationStatus: NewBusinessApplicationStatus | undefined): string => {
		switch (newBusinessApplicationStatus) {
			case NewBusinessApplicationStatus.APPROVED:
				return 'Your application is currently approved by underwriting.  Any changes you make on your application will rollback it\'s approval status and will need to be submitted again to underwriting.  Are you sure you want to continue?';
			case NewBusinessApplicationStatus.DECLINED:
				return 'Your application is currently declined.  Any changes you make on your application will rollback it\'s current status and will need to be submitted again to underwriting.  Are you sure you want to continue?';
			case NewBusinessApplicationStatus.HOLD:
				return 'Your application is currently on hold.  Any changes you make on your application will rollback it\'s current status and will need to be submitted again to underwriting.  Are you sure you want to continue?';
			case NewBusinessApplicationStatus.SUBMITTED:
				return 'Your application is currently being reviewed by underwriting.  Any changes you make on your application will rollback it\'s current status and will need to be submitted again to underwriting.  Are you sure you want to continue?';
			default:
				return String.empty();
		}
	}
);

// Get New Business Application Status Message Selector.
const makeGetNewBusinessApplicationStatusMessageSelector = () => createSelector(
	[makeGetNewBusinessApplicationStatusSelector(), makeAreAllNewBusinessApplicationSectionsCompleteSelector()],
	(newBusinessApplicationStatus: NewBusinessApplicationStatus | undefined, isNewBusinessApplicationComplete: boolean): string | undefined => {
		if (newBusinessApplicationStatus === NewBusinessApplicationStatus.SUBMITTED) {
			return 'Application is still pending underwriter approval.';
		}
		else if (!isNewBusinessApplicationComplete && newBusinessApplicationStatus === NewBusinessApplicationStatus.APPROVED) {
			return 'Application is approved, but is still incomplete.';
		}
		else if (isNewBusinessApplicationComplete && newBusinessApplicationStatus === NewBusinessApplicationStatus.APPROVED) {
			return 'Application is approved and awaiting payment.'
		}

		return undefined;
	}
);

// Get New Business Application Workflow Target Version Selector.
const makeGetNewBusinessApplicationWorkflowTargetVersionSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): number | undefined => {
		return newBusinessApplication?.newBizWorkflowTargetVersion ?? undefined;
	}
);

// Get New Business Possible Perils Selector.
const makeGetNewBusinessPossiblePerilsSelector = () => createSelector(
	[getNewBusinessApplicationState],
	(newBusinessApplicationState: NewBusinessApplicationState | undefined): PossiblePeril[] =>
		newBusinessApplicationState?.newBusinessPossiblePerils ?? [],
);

// Are New Business Possible Perils Loaded Selector.
const makeAreNewBusinessPossiblePerilsLoadedSelector = () => createSelector(
	[getNewBusinessApplicationState],
	(newBusinessApplicationState: NewBusinessApplicationState | undefined): boolean =>
		newBusinessApplicationState?.areNewBusinessPossiblePerilsLoaded ?? false,
);
// Get New Business Application Change Identifier Selector.
const makeGetNewBusinessApplicationChangeIdentifierSelector = () => createSelector(
	[
		makeGetNewBusinessApplicationSelector(),
		makeGetNewBusinessPossiblePerilsSelector(),
		makeGetNewBusinessApplicationDocumentSelector()
	],
	(
		newBusinessApplication: NewBusinessApplication | undefined,
		newBusinessPossiblePerils: PossiblePeril[],
		newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined
	): string => {
		let obj = {
			newBusinessApplication,
			newBusinessPossiblePerils,
			newBusinessApplicationDocument,
		}
		return StringService.getInstance().hashObject(obj);
	}
);


// Export composed methods.
export {
	getNewBusinessApplicationState,
};

// export selectors.
export {
	makeGetNewBusinessApplicationBroadLineOfBusinessSelector,
	makeGetNewBusinessApplicationDocumentMessageSelector,
	makeGetNewBusinessApplicationDocumentNameSelector,
	makeGetNewBusinessApplicationDocumentSelector,
	makeGetNewBusinessApplicationDocumentUrlSelector,
	makeGetNewBusinessApplicationSelector,
	makeGetNewBusinessApplicationSignatureSelector,
	makeGetNewBusinessApplicationStatusMessageSelector,
	makeGetNewBusinessApplicationStatusSelector,
	makeGetNewBusinessApplicationWorkflowTargetVersionSelector,
	makeGetNewBusinessBindingAuthorityNumberSelector,
	makeGetNewBusinessCostLineItemsSelector,
	makeGetNewBusinessDisclosureAcknowledgementsSelector,
	makeGetNewBusinessDisqualifiersAnswerSelector,
	makeGetNewBusinessDisqualifyingConditionAgreementSelector,
	makeGetNewBusinessDisqualifyingConditionAgreementVersionNumberSelector,
	makeGetNewBusinessEligibilityErrorsSelector,
	makeGetNewBusinessEligibilityResultsSelector,
	makeGetNewBusinessApplicationStatusSummarySelector,
	makeGetNewBusinessApplicationAraDecisionReasonsSelector,
	makeGetNewBusinessApplicationEligibilityStatusSelector,
	makeGetNewBusinessApplicationStatusInfoSelector,
	makeGetNewBusinessExpirationDateSelector,
	makeGetNewBusinessInceptionDateSelector,
	makeGetNewBusinessInsuredFullNameSelector,
	makeGetNewBusinessInsuredInfoSelector,
	makeGetNewBusinessPossibleDisclosureTextsSelector,
	makeGetNewBusinessPropertyStateSelector,
	makeGetNewBusinessIdSelector,
	makeGetNewBusinessSelectedPerilCodesSelector,
	makeGetNewBusinessSelectedPerilsDeductiblesSelector,
	makeGetNewBusinessTotalAnnualCostSelector,
	makeGetNewBusinessUnsubmissionConfirmationMessageSelector,
	makeHasTriaNewBusinessSelector,
	makeIsNewBusinessApplicationApprovedSelector,
	makeIsNewBusinessApplicationApprovedPaidSelector,
	makeIsNewBusinessApplicationDeclinedSelector,
	makeIsNewBusinessApplicationDocumentAvailableSelector,
	makeIsNewBusinessApplicationInTheWorksSelector,
	makeIsNewBusinessApplicationLoadedSelector,
	makeIsNewBusinessApplicationNotSubmittedSelector,
	makeIsNewBusinessApplicationOnHoldSelector,
	makeIsNewBusinessApplicationSubmittedOrHasUnderwritingDecisionSelector,
	makeIsNewBusinessApplicationSubmittedSelector,
	makeIsNewBusinessEarthquakePerilSelectedSelector,
	makeIsNewBusinessEligibilityCheckedSelector,
	makeIsNewBusinessFloodPerilSelectedSelector,
	makeGetNewBusinessPossiblePerilsSelector,
	makeAreNewBusinessPossiblePerilsLoadedSelector,
	makeGetNewBusinessApplicationChangeIdentifierSelector,
};